import { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Boxes, CircleDot, Copy, FlaskConical, IdCard, Pencil, Trash2, ZoomIn } from "lucide-react";
import { PageActions, PageBody } from "@/components/ui/PageHeader";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Panel, PanelHeader } from "@/components/ui/Panel";
import { Button, buttonStyles } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { ApprovalChip } from "@/components/domain/StatusChip";
import { LabelPrintButton, labelRef } from "@/components/domain/LabelPrintButton";
import { CustomerPricesSection } from "@/components/products/PricingPanel";
import { PriceTiersSection } from "@/components/products/PriceTiersSection";
import { ProductFilesSection } from "@/components/products/ProductFilesSection";
import { useAuth } from "@/app/auth";
import { useCan, useCanSeeCustomerRef } from "@/app/access";
import { useDeleteProduct, useProduct } from "@/api/hooks";
import { ApiClientError, getLocalizedError } from "@/lib/api";
import { formatQty, formatUnitPrice } from "@/domain/money";
import { formatDate } from "@/domain/datetime";
import { formatWireSize } from "@/domain/product";
import { formatMaterialSize } from "@hisense/shared";
import { BilingualText } from "@/components/ui/BilingualText";
import { useProductTypeZh } from "@/components/products/useProductTypeZh";
import { cn } from "@/lib/cn";
import { initialProductForm } from "@/components/products/productForm";
import { PRODUCT_FORM_GROUPS, productFieldFilled, type ProductFieldKey } from "@/components/products/productFormLayout";

function dash(value: React.ReactNode): React.ReactNode {
  if (value == null || value === "") return "—";
  return value;
}

function Meta({
  label,
  children,
  mono,
  className,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-0.5", className)}>
      <dt className="text-2xs uppercase tracking-wide text-ink-faint">{label}</dt>
      <dd className={cn("text-sm text-ink", mono && "font-mono tabular")}>{dash(children)}</dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Panel className="@container">
      <PanelHeader title={title} />
      {/* Số cột theo bề rộng CHÍNH THẺ: thẻ nằm một cột hay hai cột (màn rất rộng) đều không bị bóp. */}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 p-4 @xl:grid-cols-3 @3xl:grid-cols-4">
        {children}
      </dl>
    </Panel>
  );
}

/** Cách hiện một ô ở trang chi tiết. `null`: ô đã hiện ở đầu trang (ảnh, mã, mã phụ, mô tả). */
type DisplaySpec = { label: string; mono?: boolean; wide?: boolean; value: () => React.ReactNode } | null;

export function ProductDetailPage() {
  const { id } = useParams();
  return <ProductDetail key={id} />;
}
function ProductDetail() {
  const { t } = useTranslation();
  const typeZh = useProductTypeZh();
  const { id } = useParams();
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const listTo = `/products${search}`;
  const saved = typeof state === "object" && state !== null && "productSaved" in state && state.productSaved === true;
  const { user } = useAuth();
  const can = useCan();
  const { data: product, isLoading, error, refetch } = useProduct(id);
  const del = useDeleteProduct();
  const [priceDirty, setPriceDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actErr, setActErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<"image" | "approval" | null>(null);

  const canSeeCost = user ? can("supplier_cost:view") : false;
  const canSeeRef = useCanSeeCustomerRef();
  const canUpdate = user ? can("records:update") : false;
  const canCreate = user ? can("records:create") : false;
  const canDelete = user ? can("records:delete") : false;
  const canSetPrice = canCreate;

  usePageCrumbs(product ? [{ label: labelRef(product.productCode, product.subRef) }]
    : error ? [{ label: t("products.notFound") }] : [], listTo);

  if (isLoading) {
    return (
      <PageBody>
        <LoadingState label={t("products.loading")} />
      </PageBody>
    );
  }
  if (!product) {
    const missing =
      !error || (error instanceof ApiClientError && (error.status === 404 || error.code === "not_found"));
    return (
      <PageBody>
        <ErrorState
          message={missing ? t("products.notFound") : t("products.errorLoad")}
          onRetry={error && !missing ? () => refetch() : undefined}
        />
      </PageBody>
    );
  }

  const money = (minor: number | null) =>
    minor != null ? formatUnitPrice(minor, product.currency) : "—";

  const doDelete = async () => {
    setActErr(null);
    try {
      await del.mutateAsync(product.id);
      navigate(listTo);
    } catch (err) {
      setActErr(getLocalizedError(err, t, "products.errorDelete"));
    }
  };

  const identity = labelRef(product.productCode, product.subRef);
  const duplicateParams = new URLSearchParams(search);
  duplicateParams.set("fromProduct", product.id);
  const stockClass = product.stockQuantity < 0 ? "text-danger" : product.stockQuantity > 0 && product.reorderPoint != null && product.stockQuantity < product.reorderPoint ? "text-warn" : "text-ink";
  const previewSrc =
    preview === "approval" ? product.approvalImageUrl : preview === "image" ? product.imageUrl : null;

  const form = initialProductForm(product, product.currency);
  /** Tiếng Anh trên, tiếng Hoa dưới (Roary 09/10/2026); chưa có bản tiếng Hoa thì một dòng như cũ. */
  const bilingual = (en: string | null, zh: string | null) => (en || zh ? <BilingualText en={en} zh={zh} /> : null);
  const qty = (v: number | null) => (v != null ? formatQty(v) : null);
  const display: Record<ProductFieldKey, DisplaySpec> = {
    image: null, productCode: null, subRef: null, description: null,
    // Mã hàng của khách chỉ người được thấy (YC01-L1, 07/10/2026).
    customerRef: canSeeRef ? { label: t("products.customerRef"), mono: true, value: () => product.customerRef } : null,
    type: { label: t("products.type"), value: () => bilingual(product.type, typeZh(product.type)) },
    brandName: { label: t("products.brand"), value: () => product.brandName },
    unit: { label: t("products.unit"), value: () => product.unit },
    color: { label: t("products.color"), value: () => bilingual(product.color, product.colorZh) },
    // Màu tiếng Hoa đã nằm dưới màu tiếng Anh, không hiện thêm một ô riêng.
    colorZh: null,
    size: { label: t("products.size"), value: () => product.size },
    weightPer100: {
      label: t("products.weightPer100"), mono: true,
      value: () => (product.weightPer100 != null ? `${product.weightPer100}${product.weightUnit ? ` ${product.weightUnit}` : ""}` : null),
    },
    descriptionZh: { label: t("products.descriptionZh"), wide: true, value: () => product.descriptionZh },
    wireSize: { label: t("products.wireSize"), value: () => formatWireSize(product.wireSize) },
    materialType: { label: t("products.materialType"), value: () => product.materialType },
    materialWidth: { label: t("products.materialWidth"), mono: true, value: () => formatMaterialSize(product.materialWidth, product.materialWidthUnit) },
    materialThickness: { label: t("products.materialThickness"), mono: true, value: () => formatMaterialSize(product.materialThickness, product.materialThicknessUnit) },
    materialHardness: { label: t("products.materialHardness"), value: () => product.materialHardness },
    currency: { label: t("products.currencyLabel"), mono: true, value: () => product.currency },
    sellingPrice: { label: t("products.sellingPrice"), mono: true, value: () => money(product.sellingPrice) },
    upCharge: { label: t("products.upCharge"), mono: true, value: () => money(product.upCharge) },
    moq: { label: t("products.moq"), mono: true, value: () => qty(product.moq) },
    sampleLeadTime: { label: t("products.sampleLeadTime"), value: () => product.sampleLeadTime },
    bulkLeadTime: { label: t("products.bulkLeadTime"), value: () => product.bulkLeadTime },
    lowestPrice: { label: t("products.lowestPrice"), mono: true, value: () => money(product.lowestPrice) },
    dtdPrice: { label: t("common.dtdPrice"), mono: true, value: () => money(product.dtdPrice) },
    exwPrice: { label: t("common.exwPrice"), mono: true, value: () => money(product.exwPrice) },
    underMoqPrice: { label: t("common.underMoqPrice"), mono: true, value: () => money(product.underMoqPrice) },
    mcq: { label: t("products.mcq"), mono: true, value: () => qty(product.mcq) },
    msq: { label: t("products.msq"), mono: true, value: () => qty(product.msq) },
    supplierId: {
      label: t("products.supplier"),
      value: () => (product.supplierId
        ? <Link to={`/suppliers/${product.supplierId}`} className="text-brand hover:underline">{product.supplierName}</Link>
        : product.supplierName),
    },
    supplierProductRef: { label: t("products.supplierProductRef"), mono: true, value: () => product.supplierProductRef },
    costUnitPrice: { label: t("products.costPrice"), mono: true, value: () => money(product.costUnitPrice) },
    approvalStatus: { label: t("products.approval"), value: () => <ApprovalChip status={product.approvalStatus} /> },
    approvalImage: {
      label: t("products.approvalProof"),
      value: () => (product.approvalImageUrl ? (
        <button
          type="button"
          onClick={() => setPreview("approval")}
          className="group relative size-16 overflow-hidden rounded-sm border border-line bg-surface"
          aria-label={t("products.approvalProof")}
        >
          <img src={product.approvalImageUrl} alt="" className="size-full object-cover" />
          <span className="absolute inset-0 grid place-items-center bg-ink/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
            <ZoomIn className="size-3.5" />
          </span>
        </button>
      ) : null),
    },
    stock: {
      label: t("products.stock"), mono: true,
      // Tồn dùng được theo Kho & LOT (18/09/2026); phần chờ kiểm / không đạt ghi riêng, bấm để xem từng lô.
      value: () => (
        <>
          <Link to={`/products/${product.id}/stock${search}`} className={cn("hover:underline", stockClass)}>
            {formatQty(product.stockQuantity)}
            {product.unit ? ` ${product.unit}` : ""}
          </Link>
          {(product.stockBreakdown.pending > 0 || product.stockBreakdown.rejected > 0) && (
            <span className="block font-sans text-2xs text-ink-faint">
              {t("productStock.pending")}: {formatQty(product.stockBreakdown.pending)} · {t("productStock.rejected")}: {formatQty(product.stockBreakdown.rejected)}
            </span>
          )}
        </>
      ),
    },
    reorderPoint: { label: t("products.reorderPoint"), mono: true, value: () => qty(product.reorderPoint) },
    location: { label: t("productEditor.defaultBin"), mono: true, value: () => product.location },
    storage: { label: t("products.storage"), value: () => product.storage },
    moldRef: { label: t("products.moldRef"), value: () => product.moldRef },
    moldLocation: { label: t("products.moldLocation"), value: () => product.moldLocation },
    remarks: { label: t("common.remarks"), wide: true, value: () => product.remarks },
  };

  return (
    <PageBody className="space-y-4">
      <ConfirmDialog
        open={confirmDelete}
        title={identity}
        description={t("products.confirmDelete")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={del.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => void doDelete()}
      />
      {previewSrc && (
        <Modal
          eyebrow={t("products.imagePreview")}
          title={identity}
          closeLabel={t("common.close")}
          onClose={() => setPreview(null)}
          className="max-w-4xl"
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 items-center justify-center bg-sunken/40 p-4 sm:p-6">
              <img
                src={previewSrc}
                alt={product.description || product.productCode}
                className="max-h-[calc(100dvh-13rem)] max-w-full rounded-md object-contain shadow-[var(--shadow-card)]"
              />
            </div>
            <div className="flex shrink-0 items-center justify-end border-t border-line bg-surface px-5 py-3.5">
              <Button type="button" variant="secondary" size="sm" onClick={() => setPreview(null)}>
                {t("common.close")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={listTo} className={buttonStyles({ variant: "ghost", size: "sm" })}><ArrowLeft className="size-4" /> {t("productWorkspace.back")}</Link>
        <PageActions>
          <LabelPrintButton productRef={product.productCode} subRef={product.subRef} description={product.description} descriptionZh={product.descriptionZh} color={product.color} colorZh={product.colorZh} size={product.size} barcode={product.productCode} />
          <Link to={`/products/${product.id}/stock${search}`} className={buttonStyles({ variant: "secondary", size: "sm" })}><Boxes className="size-4" /> {t("productWorkspace.inventory")}</Link>
          {canCreate && <>
            <Link to={`/products/new?${duplicateParams}`} className={buttonStyles({ variant: "secondary", size: "sm" })}><Copy className="size-4" /> {t("productWorkspace.duplicate")}</Link>
            <Link to={`/sample-orders/new?productId=${product.id}`} className={buttonStyles({ variant: "ghost", size: "icon-sm" })} aria-label={t("products.newSampleOrderFrom")} title={t("products.newSampleOrderFrom")}><FlaskConical className="size-4" /></Link>
            <Link to={`/sample-cards?fromProduct=${product.id}`} className={buttonStyles({ variant: "ghost", size: "icon-sm" })} aria-label={t("products.newSampleCardFrom")} title={t("products.newSampleCardFrom")}><IdCard className="size-4" /></Link>
          </>}
          {canUpdate && <Link to={`/products/${product.id}/edit${search}`} className={buttonStyles({ variant: "secondary", size: "sm" })}><Pencil className="size-4" /> {t("common.edit")}</Link>}
          {canDelete && <Button variant="danger" size="icon-sm" aria-label={t("common.delete")} title={t("common.delete")} disabled={del.isPending || priceDirty} onClick={() => setConfirmDelete(true)}><Trash2 className="size-4" /></Button>}
        </PageActions>
      </div>
      {saved && <p role="status" className="rounded-sm bg-ok-tint px-3 py-2 text-sm text-ok">{t("common.saved")}</p>}

      {actErr && (
        <p className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{actErr}</p>
      )}

      <Panel>
        <div className="flex items-center gap-4 p-4 sm:gap-5 sm:p-5">
          {product.imageUrl ? (
            <button
              type="button"
              onClick={() => setPreview("image")}
              className="group relative size-24 shrink-0 overflow-hidden rounded-sm border border-line bg-sunken focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand"
              aria-label={`${t("products.viewImage")}: ${product.productCode}`}
            >
              <img src={product.imageUrl} alt="" className="size-full object-cover" />
              <span className="absolute inset-0 grid place-items-center bg-ink/50 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                <ZoomIn className="size-4" />
              </span>
            </button>
          ) : (
            <div className="grid size-24 shrink-0 place-items-center rounded-sm border border-line bg-brand-tint text-brand-ink">
              <CircleDot className="size-6" />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
            <h1 className="break-words font-mono tabular text-2xl font-semibold tracking-tight text-ink">{identity}</h1>
            {/* Mô tả hai thứ tiếng ở đầu trang: tiếng Anh trên, tiếng Hoa dưới (Roary 09/10/2026). */}
            <BilingualText className="text-sm text-ink-soft" en={product.description} zh={product.descriptionZh} />
            <div className="flex flex-wrap items-center gap-3">
              <ApprovalChip status={product.approvalStatus} />
              <span
                className={cn(
                  "font-mono tabular text-sm",
                  stockClass,
                )}
              >
                <span className="mr-2 font-sans text-xs text-ink-soft">{t("products.stock")}</span>{formatQty(product.stockQuantity)}
                {product.unit ? <span className="ml-1 text-ink-faint">{product.unit}</span> : null}
              </span>
              <span className="text-sm text-ink"><span className="mr-2 text-xs text-ink-soft">{t("productWorkspace.listPrice")}</span><span className="font-mono tabular">{product.sellingPrice == null ? t("productWorkspace.noPrice") : `${formatUnitPrice(product.sellingPrice, product.currency, false)} ${product.currency}`}</span></span>
            </div>
          </div>
        </div>
      </Panel>

      {/*
        Cùng 5 nhóm, cùng thứ tự với biểu mẫu Sửa sản phẩm (productFormLayout.ts, 19/09/2026). Trường ít dùng (mục gập
        của biểu mẫu) có dữ liệu thì hiện; chưa có thì gom thành một dòng "Chưa điền" cuối nhóm — không trường nào mất dấu.
      */}
      <div className="grid items-start gap-4 2xl:grid-cols-2">
        {(["left", "right"] as const).map((column) => (
          <div key={column} className="min-w-0 space-y-4">
            {PRODUCT_FORM_GROUPS.filter((g) => g.column === column && (!g.costOnly || canSeeCost)).map((g) => {
              const main = [...(g.aside ? [g.aside] : []), ...g.fields.map((f) => f.key)].filter((k) => display[k]);
              const more = (g.more ?? []).flatMap((m) => m.fields.map((f) => f.key)).filter((k) => display[k]);
              const shown = (k: ProductFieldKey) => productFieldFilled(form, k);
              const emptyMore = more.filter((k) => !shown(k));
              return (
                <Section key={g.id} title={t(`productEditor.groups.${g.id}`)}>
                  {[...main, ...more.filter(shown)].map((k) => {
                    const spec = display[k]!;
                    return (
                      <Meta key={k} label={spec.label} mono={spec.mono} className={spec.wide ? "col-span-full" : undefined}>
                        {spec.value()}
                      </Meta>
                    );
                  })}
                  {g.id === "control" && (
                    <Meta label={t("products.created")} mono>
                      {`${formatDate(product.createdAt)}${product.createdByName ? ` · ${product.createdByName}` : ""}`}
                    </Meta>
                  )}
                  {emptyMore.length > 0 && (
                    <p className="col-span-full text-2xs text-ink-faint">
                      {t("productEditor.emptyMore", { fields: emptyMore.map((k) => display[k]!.label).join(" · ") })}
                    </p>
                  )}
                </Section>
              );
            })}
          </div>
        ))}
      </div>

      <Panel className="p-4">
        <CustomerPricesSection
          productId={product.id}
          currency={product.currency}
          sellingPrice={product.sellingPrice}
          canSet={canSetPrice}
          onDirtyChange={setPriceDirty}
        />
      </Panel>

      <Panel className="p-4">
        <ProductFilesSection productId={product.id} isAdmin={user?.role === "admin"} />
      </Panel>

      <Panel className="p-4">
        <PriceTiersSection
          productId={product.id}
          currency={product.currency}
          canSet={canSetPrice}
        />
      </Panel>
    </PageBody>
  );
}
